import { Box, Stack, Typography } from '@mui/material'
import { C1Button } from 'c1ui/components/C1Button'
import { C1DateTimePicker } from 'components/core/date-time-picker/C1DateTimePicker'
import { C1LabeledRadioGroup } from 'components/core/RHKFormComponents/C1LabeledRadioGroup'
import { SelectOneUserHook } from 'components/core/search/users'
import { UserValue } from 'lib/actions/user'
import { FC } from 'react'
import { Control, Controller, FieldErrors, UseFormClearErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'

import { DelegationType, UserDelegationFormData } from './user-delegate-types'

interface DelegateFormSectionsProps {
    control: Control<UserDelegationFormData>
    errors: FieldErrors<UserDelegationFormData>
    watch: UseFormWatch<UserDelegationFormData>
    setValue: UseFormSetValue<UserDelegationFormData>
    clearErrors: UseFormClearErrors<UserDelegationFormData>
    userId: string
}

export const DelegateFormSections: FC<DelegateFormSectionsProps> = ({
    control,
    errors,
    watch,
    setValue,
    clearErrors,
    userId,
}) => {
    const intl = useIntl()
    const formValues = watch()

    const delegateOptions = [
        {
            id: 'permanent-delegate',
            value: DelegationType.PERMANENT,
            label: (
                <Stack spacing={2}>
                    <Stack spacing={1}>
                        <FormattedMessage
                            id="component_access_review_onetime_type_label"
                            defaultMessage="Permanent"
                        />
                        <Typography variant="caption" color="text.secondary">
                            <FormattedMessage
                                id="component_access_review_onetime_type_description"
                                defaultMessage="Assigns tasks to another user on an ongoing basis."
                            />
                        </Typography>
                    </Stack>
                    {formValues.delegationType === DelegationType.PERMANENT &&
                        <Box width="360px">
                            <Controller
                                name="delegatedUserId"
                                control={control}
                                render={({ field }): React.ReactElement => (
                                    <SelectOneUserHook
                                        id="delegatedUserId"
                                        initUserId={field.value || undefined}
                                        onSelected={(value: UserValue | null): void => {
                                            field.onChange(value?.id || null)
                                        }}
                                        hideLabel
                                        placeholder={intl.formatMessage({
                                            id: 'component_select_delegated_user_placeholder',
                                            defaultMessage: 'Select a user',
                                        })}
                                        baseRequest={{
                                            excludeIds: [userId],
                                        }}
                                        error={!!errors.delegatedUserId}
                                        helperText={errors.delegatedUserId?.message}
                                    />
                                )}
                            />
                        </Box>
                    }
                </Stack>
            ),
            sx: { margin: 2, alignItems: 'flex-start' },
        },
        {
            id: 'temporary-delegate',
            value: DelegationType.TEMPORARY,
            label: (
                <Stack spacing={2}>
                    <Stack spacing={1}>
                        <FormattedMessage
                            id="component_access_review_recurring_type_label"
                            defaultMessage="Temporary"
                        />
                        <Typography variant="caption" color="text.secondary">
                            <FormattedMessage
                                id="component_access_review_recurring_type_description"
                                defaultMessage="Assigns your tasks to another user for a limited time, such as when you are out of office."
                            />
                        </Typography>
                    </Stack>
                    {formValues.delegationType === DelegationType.TEMPORARY && (
                        // user selector
                        <Stack spacing={4}>
                            <Box width="360px">
                                <Controller
                                    name="delegatedUserId"
                                    control={control}
                                    render={({ field }): React.ReactElement => (
                                        <SelectOneUserHook
                                            id="delegatedUserId"
                                            initUserId={field.value || undefined}
                                            onSelected={(value: UserValue | null): void => {
                                                field.onChange(value?.id || null)
                                            }}
                                            hideLabel
                                            placeholder={intl.formatMessage({
                                                id: 'component_select_delegated_user_placeholder',
                                                defaultMessage: 'Select a user',
                                            })}
                                            baseRequest={{
                                                excludeIds: [userId],
                                            }}
                                            error={!!errors.delegatedUserId}
                                            helperText={errors.delegatedUserId?.message}
                                        />
                                    )}
                                />
                            </Box>
                            {/* date pickers */}
                            <Stack direction="row" spacing={6}>
                                <Controller
                                    name="startAt"
                                    control={control}
                                    render={({ field }): React.ReactElement => (
                                        <C1DateTimePicker
                                            label={intl.formatMessage({
                                                id: 'user_details_delegate_select_start_date',
                                                defaultMessage: 'Start date',
                                            })}
                                            value={field.value}
                                            onChange={field.onChange}
                                            closeOnSelect={false}
                                            slotProps={{
                                                textField: {
                                                    placeholder: intl.formatMessage({
                                                        id: 'user_details_delegate_select_start_date',
                                                        defaultMessage: 'Select start date',
                                                    }),
                                                    error: !!errors.startAt,
                                                    helperText: errors.startAt?.message,
                                                },
                                            }}
                                        />
                                    )}
                                />
                                <Stack>
                                    <Controller
                                        name="expirationAt"
                                        control={control}
                                        render={({ field }): React.ReactElement => (
                                            <C1DateTimePicker
                                                label={intl.formatMessage({
                                                    id: 'user_details_delegate_select_end_date',
                                                    defaultMessage: 'End date',
                                                })}
                                                value={field.value}
                                                onChange={field.onChange}
                                                minDateTime={formValues.startAt ? formValues.startAt : new Date()}
                                                closeOnSelect={false}
                                                slotProps={{
                                                    textField: {
                                                        placeholder: intl.formatMessage({
                                                            id: 'user_details_delegate_select_end_date',
                                                            defaultMessage: 'Select end date',
                                                        }),
                                                        error: !!errors.expirationAt,
                                                        helperText: errors.expirationAt?.message,
                                                    },
                                                }}
                                            />
                                        )}
                                    />
                                    <Stack direction="row" spacing={1}>
                                        {formValues.expirationAt && (
                                            <C1Button
                                                sx={{ alignSelf: 'center' }}
                                                variant="text"
                                                onClick={(): void => {
                                                    setValue('expirationAt', null)
                                                    clearErrors('expirationAt')
                                                }}
                                            >
                                                <FormattedMessage id="end_date_clear_button" defaultMessage="Clear" />
                                            </C1Button>
                                        )}
                                    </Stack>
                                </Stack>
                            </Stack>
                        </Stack>
                    )}
                </Stack>
            ),
            sx: { margin: 2, alignItems: 'flex-start' },
        },
        {
            id: 'no-delegate',
            value: DelegationType.NO_DELEGATE,
            label: (
                <Stack spacing={2}>
                    <FormattedMessage
                        id="component_access_review_no_delegate_label"
                        defaultMessage="No delegate"
                    />
                    <Typography variant="caption" color="text.secondary">
                        <FormattedMessage
                            id="component_access_review_no_delegate_description"
                            defaultMessage="Your tasks are not reassigned."
                        />
                    </Typography>
                </Stack>
            ),
            sx: { margin: 2, alignItems: 'flex-start' },
        },
    ]

    return (
        <C1LabeledRadioGroup
            control={control}
            name="delegationType"
            error={!!errors.delegationType}
            helperText={errors.delegationType?.message}
            onChange={(e): void => {
                const value = e.target.value as DelegationType
                const previousValue = formValues.delegationType

                setValue('delegationType', value)

                // Clear fields and errors when switching delegation types
                if (value === DelegationType.NO_DELEGATE) {
                    setValue('delegatedUserId', null)
                    setValue('startAt', null)
                    setValue('expirationAt', null)
                    clearErrors(['delegatedUserId', 'startAt', 'expirationAt'])
                } else if (value === DelegationType.PERMANENT && previousValue === DelegationType.TEMPORARY) {
                    setValue('startAt', null)
                    setValue('expirationAt', null)
                    clearErrors(['startAt', 'expirationAt'])
                } else if (value === DelegationType.TEMPORARY && previousValue === DelegationType.PERMANENT) {
                    clearErrors(['startAt', 'expirationAt'])
                } else if (previousValue === DelegationType.NO_DELEGATE) {
                    clearErrors(['delegatedUserId', 'startAt', 'expirationAt'])
                }
            }}
            options={delegateOptions}
        />
    )
}
